// metronic_8 behaviour-contract overrides.
//
// Inherits bootstrap5's bootstrap-flavoured adapters (modal, collapse,
// toast, popover, blockUI, drawer, submenu, breakpoint, imageInput stub,
// passwordMeter stub) and replaces seven hooks with Metronic-native (KT /
// Tagify) implementations. Wrapping the underlying widget directly preserves
// the runtime behaviour metronic_8 has today after base consumers route
// through hm.behaviors instead of calling the globals directly.
//
// Replaces bootstrap5's module via the theme's index.js:
//   hiveMind.behaviors = require('./scripts/behaviors');
//
// Hooks overridden here:
//   - submenu       — KTMenu (selectSearchable's options dropdown).
//   - drawer        — KTDrawer (itemsList's mobile aside).
//   - blockUI       — KTBlockUI (component's "blocked while async" overlay).
//   - breakpoint    — KTUtil.isBreakpointUp (itemsList's aside-vs-drawer switch).
//   - imageInput    — KTImageInput (image component's file picker).
//   - passwordMeter — KTPasswordMeter (password component's visibility/strength).
//   - tags          — Tagify (selectMultiple's tags input).
//
// All these globals (KTMenu, KTDrawer, KTBlockUI, KTUtil, KTImageInput,
// KTPasswordMeter, Tagify) are loaded into window by Metronic's
// plugins.bundle.js before app boot — accessed lazily inside the hook methods
// so this module is safe to require at module-load time.

// Inherit bootstrap5's adapter set as the baseline.
const bootstrap5behaviors = require('../../../../core/themes/bootstrap5/scripts/behaviors');

// base tier's helper module — required directly so the drawer adapter can use
// the same theme-mapped show/hide/isHidden behaviour the rest of the base tier uses
// (helper.hm is set during hiveMind.main() via adopt(), so calling
// helper.showElement / hideElement / isHidden resolves the displayNone cssClass
// correctly under any leaf).
const helper = require('../../../../core/themes/base/scripts/helper');

// Map base's neutral lifecycle event names to KTMenu's namespaced names.
const KT_MENU_EVENTS = {
    shown: 'kt.menu.dropdown.shown',
    hidden: 'kt.menu.dropdown.hidden'
};

// Mirror zit-namespaced KTMenu attributes to kt-namespaced ones across the
// document so KTMenu's auto-discovery (via KTMenu.createInstances() /
// KTMenu.init()) finds the markup after Step 5g renames data-kt-menu* →
// data-zit-menu* in base templates. Pre-5g this is a no-op (no
// [data-zit-menu*] elements exist); post-5g it bridges the gap. The
// per-element copy is delegated to helper.mirrorDataAttribute, which is
// idempotent (only writes when the destination key is unset). Called from
// submenu.createInstances() (for dynamic markup) and from m8 app.initTheme
// before the boot-time KTComponents.init() scan.
const mirrorMenuAttrs = function () {
    // Wrapper-level marker on the menu element itself (data-zit-menu -> data-kt-menu).
    document.querySelectorAll('[data-zit-menu]').forEach((el) => helper.mirrorDataAttribute(el, 'menu', 'kt'));
    // Per-trigger menu options (trigger / toggle / placement) on whichever
    // element acts as the menu toggle — selectSearchable's inputHolder,
    // actionsGroup's button, etc.
    document.querySelectorAll('[data-zit-menu-trigger]').forEach((el) => helper.mirrorDataAttribute(el, 'menuTrigger', 'kt'));
    document.querySelectorAll('[data-zit-menu-toggle]').forEach((el) => helper.mirrorDataAttribute(el, 'menuToggle', 'kt'));
    document.querySelectorAll('[data-zit-menu-placement]').forEach((el) => helper.mirrorDataAttribute(el, 'menuPlacement', 'kt'));
};

// metronic_8-specific overrides. Spread first onto bootstrap5's baseline
// so any hook NOT redefined here (modal/collapse/toast/popover) inherits.
const behaviors = Object.assign({}, bootstrap5behaviors, {

    // Submenu via KTMenu. KTMenu supports anchoring the menu to a remote
    // trigger element (selectSearchable relies on this), which Bootstrap's
    // Dropdown does not.
    submenu: {
        // Bulk-init any KTMenu markup on the page. Mirrors zit-namespaced
        // attributes to kt-namespaced ones first (no-op pre Step 5g, active
        // post-rename) so KTMenu finds the markup. Idempotent on both sides.
        createInstances: function () {
            mirrorMenuAttrs();
            KTMenu.createInstances();
        },
        // Retrieve the KTMenu instance attached to el (the menu element, not
        // the trigger — KTMenu's API is keyed on the menu).
        get: function (el) {
            // KTMenu.getInstance returns null for elements that haven't been initialised.
            const ktInstance = KTMenu.getInstance(el);
            if (ktInstance === null || typeof ktInstance === 'undefined') {
                return null;
            }
            // Return an adapter that exposes the contract surface (on / show)
            // mapping neutral event names to KT's namespaced ones.
            return {
                // Subscribe to lifecycle events using neutral names.
                on: function (eventName, handler) {
                    // Translate the neutral event name to KT's emission.
                    const ktEventName = KT_MENU_EVENTS[eventName];
                    if (typeof ktEventName === 'undefined') {
                        // Fail loudly so callers don't silently miss events.
                        throw new Error('zit submenu.on: unknown event "' + eventName + '"');
                    }
                    // Wire the handler on the KTMenu instance.
                    ktInstance.on(ktEventName, handler);
                },
                // Anchor the submenu against targetEl and open it.
                show: function (targetEl) {
                    ktInstance.show(targetEl);
                }
            };
        }
    },

    // Drawer via KTDrawer. Owns the full aside-vs-drawer lifecycle:
    // - At the lg breakpoint and above, the host pins inline as an aside
    //   (Metronic shifts the page grid via `app-aside` class on the host
    //   plus the `kt-app-aside-enabled` / `kt-app-aside-fixed` body-dataset
    //   attributes). KTDrawer's slide animation is NOT used — the host
    //   simply becomes visible alongside the page.
    // - Below lg, the host floats as a slide-in overlay. KTDrawer drives the
    //   animation; the body-dataset attributes stay false so the page
    //   remains single-column.
    //
    // The body-dataset attributes are GLOBAL state (they apply to whichever
    // aside the page-layout currently hosts). To keep them correct
    // independent of iteration order across multiple drawers, refreshBody()
    // recomputes them by querying the DOM for any visible aside currently
    // marked `app-aside` — the consumer never needs to think about ordering.
    drawer: {
        // Retrieve the KTDrawer instance attached to el.
        get: function (el) {
            // KTDrawer.getInstance returns null if the drawer hasn't been initialised.
            const ktInstance = KTDrawer.getInstance(el);
            if (ktInstance === null || typeof ktInstance === 'undefined') {
                return null;
            }

            // Recompute body-dataset state from the current DOM. Body data
            // says "is ANY aside currently pinned inline" — derived from
            // visible elements that carry the `app-aside` class. Run after
            // any setHostMode change so iteration order across multiple
            // drawers doesn't matter (body-data flips to true the moment any
            // aside is inline + visible, and back to false when none are).
            const refreshBody = function () {
                // Resolve the theme's displayNone class so the :not() selector
                // matches whatever class helper.hideElement adds (under m8 it's d-none).
                const displayNoneClass = helper.hm.app.cssClass('displayNone');
                // Any visible inline aside on the page?
                const anyInline = document.querySelector('.app-aside:not(.' + displayNoneClass + ')') !== null;
                // Set both flags Metronic's layout watches.
                document.body.dataset.ktAppAsideEnabled = anyInline ? 'true' : 'false';
                document.body.dataset.ktAppAsideFixed = anyInline ? 'true' : 'false';
            };

            // Pin the host to layout (inline mode). Adds the class first,
            // then refreshes body-data — the host must already be visible
            // for refreshBody's :not(displayNone) selector to count it.
            const pinInline = function () {
                el.classList.add('app-aside');
                refreshBody();
            };

            // Unpin the host (back to overlay mode or fully closed). Removes
            // the class, then refreshes body-data so it drops to false unless
            // some other aside is still pinned visible.
            const unpinInline = function () {
                el.classList.remove('app-aside');
                refreshBody();
            };

            // Public: display the drawer. Picks the layout mode based on
            // current viewport — inline above lg, overlay below.
            const show = function () {
                // Idempotent: already visible, nothing to do.
                if (!helper.isHidden(el)) {
                    return;
                }
                if (KTUtil.isBreakpointUp('lg')) {
                    // Inline: reveal the host first so the body-data refresh
                    // (inside pinInline) can detect it as visible+inline.
                    helper.showElement(el);
                    pinInline();
                } else {
                    // Overlay: ensure host is overlay-only, then animate slide-in.
                    unpinInline();
                    helper.showElement(el);
                    ktInstance.show();
                }
            };

            // Public: hide the drawer. Mirrors show() with reverse order in
            // overlay mode (animation completes before display:none).
            const hide = function () {
                // Idempotent: already hidden, just make sure the host's
                // inline state is cleared (in case viewport just changed
                // and the page still thinks an aside is pinned).
                if (helper.isHidden(el)) {
                    unpinInline();
                    return;
                }
                if (KTUtil.isBreakpointUp('lg')) {
                    // Inline: hide host, then drop pinned state so the page
                    // grid returns to single-column.
                    helper.hideElement(el);
                    unpinInline();
                } else {
                    // Overlay: play the slide-out animation, then hide host.
                    ktInstance.hide();
                    helper.hideElement(el);
                    unpinInline();
                }
            };

            // Public: toggle visibility based on current state.
            const toggle = function () {
                if (helper.isHidden(el)) {
                    show();
                } else {
                    hide();
                }
            };

            // Public: release listeners. KTDrawer ships destroy on the
            // instance; guard defensively in case a future Metronic
            // version changes the surface.
            const destroy = function () {
                if (typeof ktInstance.destroy === 'function') {
                    ktInstance.destroy();
                }
            };

            return {
                show: show,
                hide: hide,
                toggle: toggle,
                destroy: destroy
            };
        }
    },

    // BlockUI via KTBlockUI. Metronic's KT widget paints the "blocked"
    // state with its own DOM and CSS — preferred over bootstrap5's
    // CSS-only fallback to keep visuals identical to today's m8 pages.
    blockUI: {
        create: function (el) {
            // Construct a fresh KTBlockUI bound to el. The component's block()
            // method is non-idempotent (each call paints another overlay), so
            // the consumer is expected to keep a single instance per element.
            const ktInstance = new KTBlockUI(el);
            // Adapter mapping contract method names to KT's (already aligned).
            return {
                // Apply the blocked overlay.
                block: function () { ktInstance.block(); },
                // Remove the overlay (instance still reusable).
                release: function () { ktInstance.release(); },
                // Tear down listeners and residual DOM.
                destroy: function () { ktInstance.destroy(); }
            };
        }
    },

    // Breakpoint via KTUtil.isBreakpointUp. Same Bootstrap-scale breakpoints
    // bootstrap5's matchMedia adapter uses, but routed through KTUtil to
    // pick up its viewport-width calculation (includes scrollbar quirks
    // matchMedia does not). Behavior-preserving for itemsList's aside switch.
    breakpoint: {
        isUp: function (name) {
            return KTUtil.isBreakpointUp(name);
        }
    },

    // Image-input via KTImageInput. KT's widget auto-discovers its triggers
    // via data-kt-image-input-action attributes on descendants of the
    // wrapper. Pre Step 5g rename, base markup carries those data-kt-*
    // attributes and KT auto-init from KTComponents.init() also fires; KT's
    // _construct() is idempotent (reuses any existing instance bound via
    // KTUtil.data), so an explicit create() call here is safe before or
    // after the rename.
    imageInput: {
        create: function (el) {
            // Mirror zit-namespaced attributes to kt-namespaced ones so KT's
            // auto-discovery still finds the wrapper marker and trigger
            // buttons after Step 5g renames the markup. helper.mirrorDataAttribute
            // is idempotent — only writes when the destination key is unset.
            helper.mirrorDataAttribute(el, 'imageInput', 'kt');
            // Mirror per-trigger action attributes (change / cancel / remove).
            const triggers = el.querySelectorAll('[data-zit-image-input-action]');
            triggers.forEach((triggerEl) => helper.mirrorDataAttribute(triggerEl, 'imageInputAction', 'kt'));

            // Construct the KT widget. Idempotent — _construct() reuses the
            // instance bound via KTUtil.data(el).get('image-input') if any.
            new KTImageInput(el);

            // Adapter return surface. KTImageInput exposes no public dispose,
            // and the consumer (image component) doesn't tear elements down
            // independently — form teardown removes the markup, listeners go
            // with it. dispose() is a no-op to satisfy the contract.
            return {
                dispose: function () { /* see comment above */ }
            };
        }
    },

    // Password-meter via KTPasswordMeter. Same idempotent-construct +
    // attribute-mirroring pattern as imageInput.
    passwordMeter: {
        create: function (el) {
            // Mirror the wrapper marker (data-zit-password-meter -> data-kt-password-meter).
            helper.mirrorDataAttribute(el, 'passwordMeter', 'kt');
            // Mirror the per-trigger control attributes (visibility / highlight).
            const controls = el.querySelectorAll('[data-zit-password-meter-control]');
            controls.forEach((controlEl) => helper.mirrorDataAttribute(controlEl, 'passwordMeterControl', 'kt'));

            // Construct the KT widget (idempotent via KTUtil.data binding).
            new KTPasswordMeter(el);

            // No public dispose on KTPasswordMeter — markup teardown is the
            // form's responsibility.
            return {
                dispose: function () { /* see comment above */ }
            };
        }
    },

    // Tag-input via Tagify (window.Tagify, loaded by Metronic's
    // plugins.bundle.js). This adapter owns all the Tagify-specific config
    // that used to live in selectMultiple.js — the component now only passes
    // its option list + free-tagging flag (config.options / config.freeTags).
    // Returns the contract handle (getValues / selectAll / selectNone) so base
    // selectMultiple stays framework-neutral.
    tags: {
        create: function (el, config) {
            const options = config.options || [];
            const freeTags = !!config.freeTags;

            // Whitelist from the component's options ({ value, name } per Tagify).
            const whiteList = options.map((option) => ({ value: option.id, name: option.name }));

            const tagifyConfigs = {
                tagTextProp: 'name',
                whitelist: whiteList,
                // Whitelist is enforced only outside free-tagging mode; free tags
                // accept any typed value (the whitelist then acts only as
                // autocomplete suggestions).
                enforceWhitelist: !freeTags,
                dropdown: {
                    closeOnSelect: false,
                    enabled: 0,
                    maxItems: 20,
                    searchKeys: ['name'],
                    mapValueTo: (data) => { return String(data.name); }
                },
                // Resolve each tag's display text from the option list; an
                // unmatched value is a legitimate user tag in free-tagging mode
                // (show the typed text), otherwise flag it as not whitelisted.
                transformTag: (tagData) => {
                    let found = false;
                    for (let i = 0; i < options.length; i++) {
                        if (String(tagData.value) === options[i].id) {
                            found = options[i];
                            break;
                        }
                    }

                    if (found !== false) {
                        tagData.name = found.name;
                    } else {
                        tagData.name = freeTags ? tagData.value : '-- not found --';
                    }
                },
                // The hidden input value is the comma-joined list of selected ids.
                originalInputValueFormat: (valuesArr) => valuesArr.map((item) => item.value).join(',')
            };

            const tagify = new window.Tagify(el, tagifyConfigs);

            // Contract handle — base selectMultiple calls only these.
            return {
                // Current selection as an array of value strings.
                getValues: function () {
                    return tagify.value.map((selected) => selected.value);
                },
                // Select every whitelisted option.
                selectAll: function () {
                    tagify.dropdown.selectAll();
                },
                // Clear the selection.
                selectNone: function () {
                    tagify.removeAllTags();
                }
            };
        }
    },

    // Date / datetime picker via tempusDominus, overriding base's native input.
    // Keeps metronic's localized display while honouring the ISO value contract:
    // the input is seeded from / read back as ISO, so the server always receives
    // ISO regardless of the displayed format. Shared by date, datetime and
    // date_interval (each end). tempusDominus is loaded into window by Metronic's
    // plugins.bundle.js before app boot.
    datepicker: {
        // (el, {time, minDate, maxDate, format, locale, startOfTheWeek, disabledDaysOfTheWeek})
        //   -> { getValue(): isoString, setMin(iso|''), setMax(iso|''), dispose() }
        create: function (el, opts) {
            opts = opts || {};

            const TD = window.tempusDominus;

            // Zero-pad to 2 digits for ISO assembly.
            const pad = (n) => (n < 10 ? '0' + n : '' + n);

            // ISO string -> a local-time DateTime tempusDominus can use. Date-only values
            // get a midnight time component so they are not parsed as UTC (which would shift
            // the day in negative timezones).
            const isoToDateTime = (iso) => {
                if (!iso) {
                    return undefined;
                }
                const normalised = iso.indexOf('T') === -1 ? iso + 'T00:00:00' : iso;
                const dt = new TD.DateTime(normalised);
                // Guard unparseable / zero values ('0000-00-00', defaults, blanks): an Invalid
                // Date makes tempusDominus throw a RangeError in Intl.DateTimeFormat.format().
                return isNaN(dt.getTime()) ? undefined : dt;
            };

            // tempusDominus expects the disabled weekdays as 0-6; normalise the 1-7 input.
            let daysOfWeekDisabled = [];
            (opts.disabledDaysOfTheWeek || []).forEach((dayNo) => {
                if (dayNo > 6) {
                    dayNo -= 7;
                }
                daysOfWeekDisabled.push(dayNo);
            });

            // base rendered a native date/datetime-local input; tempusDominus drives a
            // read-only text field, so switch the type and clear the ISO value before init
            // (otherwise tempusDominus tries to parse the ISO string as the display format).
            const initialIso = el.value;
            el.type = 'text';
            el.readOnly = true;
            el.autocomplete = 'off';
            el.value = '';

            const initParams = {
                display: {
                    components: {
                        calendar: true,
                        date: true,
                        month: true,
                        year: true,
                        decades: true,
                        clock: opts.time,
                        hours: opts.time,
                        minutes: opts.time,
                        seconds: false,
                        useTwentyfourHour: undefined
                    },
                    buttons: {
                        today: true,
                        clear: true,
                        close: true
                    }
                },
                localization: {
                    startOfTheWeek: opts.startOfTheWeek,
                    format: opts.format,
                    locale: opts.locale
                },
                restrictions: {
                    minDate: isoToDateTime(opts.minDate),
                    maxDate: isoToDateTime(opts.maxDate),
                    daysOfWeekDisabled: daysOfWeekDisabled
                }
            };

            const picker = new TD.TempusDominus(el, initParams);

            // Seed the initial selection from the ISO value (renders in the localized format).
            // Skip unparseable values so tempusDominus never formats an Invalid Date.
            const seed = isoToDateTime(initialIso);
            if (seed !== undefined) {
                picker.dates.setValue(seed, 0);
            }

            return {
                // Read the picked date straight from the picker and rebuild ISO from its
                // local components — never the displayed (localized) text.
                getValue: function () {
                    const picked = picker.dates.picked[0];
                    if (!picked) {
                        return '';
                    }
                    let iso = picked.getFullYear() + '-' + pad(picked.getMonth() + 1) + '-' + pad(picked.getDate());
                    if (opts.time) {
                        iso += 'T' + pad(picked.getHours()) + ':' + pad(picked.getMinutes()) + ':' + pad(picked.getSeconds());
                    }
                    return iso;
                },
                setMin: function (iso) {
                    picker.updateOptions({ restrictions: { minDate: isoToDateTime(iso) } });
                },
                setMax: function (iso) {
                    picker.updateOptions({ restrictions: { maxDate: isoToDateTime(iso) } });
                },
                dispose: function () {
                    picker.dispose();
                }
            };
        }
    }
});

module.exports = behaviors;
